import React, { useState, useCallback } from 'react';
import { Button, Upload, message, List, Tooltip, Typography, Popconfirm, Image, Tag, Space } from 'antd';
import { PaperClipOutlined, DeleteOutlined, FilePdfOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import fileStorageService, { CYCLE_ATTACHMENTS_BUCKET } from '../../services/fileStorageService';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import useAuth from '../../hooks/useAuth';
import { useSecureRequest } from '../../hooks/useSecureRequest';

const { Text } = Typography;

export interface AttachmentItem {
  id: number;
  uid: string;
  file_name: string;
  file_type: 'image' | 'pdf';
  file_url: string;
  storage_path: string;
  description?: string;
  log_input_id?: number | null;
  rawFile?: File;
}

interface CycleLogAttachmentsProps {
  logId?: number; // Si no se provee, funciona en modo 'unbound' (diferido)
  logUid?: string;
  cycleUid?: string;
  logInputId?: number | null;
  existingAttachments?: AttachmentItem[];
  canEdit?: boolean;
  value?: AttachmentItem[]; // Para integración con Form.Item
  onChange?: (attachments: AttachmentItem[]) => void; // Para integración con Form.Item
  onAttachmentAdded?: (attachment: AttachmentItem) => void;
  onAttachmentDeleted?: (attachmentId: number) => void;
  label?: string;
}

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']);
const MAX_FILE_SIZE_MB = 10;

const CycleLogAttachments: React.FC<CycleLogAttachmentsProps> = ({
  logId,
  logUid,
  cycleUid = 'temp_cycle',
  logInputId,
  existingAttachments = [],
  canEdit = true,
  value,
  onChange,
  onAttachmentAdded,
  onAttachmentDeleted,
  label,
}) => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const { securePost } = useSecureRequest();
  const [uploading, setUploading] = useState(false);
  
  // Usamos value (controlado) o el listado de existentes
  const displayAttachments = value || existingAttachments;
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const getFileType = (file: File): 'image' | 'pdf' => {
    return file.type === 'application/pdf' ? 'pdf' : 'image';
  };

  const beforeUpload = useCallback((file: RcFile) => {
    if (!ALLOWED_TYPES.has(file.type)) {
      message.error('Solo se permiten imágenes (JPG, PNG, WebP, GIF) y archivos PDF.');
      return Upload.LIST_IGNORE;
    }
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > MAX_FILE_SIZE_MB) {
      message.error(`El archivo no puede superar ${MAX_FILE_SIZE_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    return true; // Continuar con customRequest
  }, []);

  const handleUpload = useCallback(async (file: File) => {
    // Si NO hay logId (estamos creando un nuevo log en el formulario), usamos carga diferida
    if (!logId) {
      const localUrl = URL.createObjectURL(file);
      const newAttachment: AttachmentItem = {
        id: Date.now() + Math.floor(Math.random() * 1000),
        uid: `tmp_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        file_name: file.name,
        file_type: getFileType(file),
        file_url: localUrl,
        storage_path: '',
        rawFile: file,
        log_input_id: logInputId || null,
      };
      onChange?.([...displayAttachments, newAttachment]);
      message.success('Archivo adjuntado (se subirá al guardar)');
      return;
    }

    // Modo directo (cuando logId ya existe, ej: agregando a un log existente)
    const timestamp = Date.now();
    const sanitizedName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, '_');
    const safeLogUid = logUid || 'general';
    const inputSuffix = logInputId ? `input_${logInputId}` : 'general';
    const basePath = `${company.current?.uid}/${cycleUid}/logs/${safeLogUid}`;
    const filename = `${inputSuffix}_${timestamp}_${sanitizedName}`;
    const storagePath = `${basePath}/${filename}`;

    try {
      setUploading(true);
      const { url, storagePath: finalPath } = await fileStorageService.uploadFile(
        tokenData,
        CYCLE_ATTACHMENTS_BUCKET,
        storagePath,
        file
      );

      const fileDef = {
        log_id: logId,
        log_input_id: logInputId || null,
        file_name: file.name,
        file_type: getFileType(file),
        file_url: url,
        storage_path: finalPath,
      };

      const res = await securePost('/manufacturing/add-log-attachment', {
        company_uid: company.current?.uid,
        attachment: fileDef,
        csrfToken: tokenData?.token?.uid,
      });

      if (res?.attachment) {
        const newAttachment = res.attachment as AttachmentItem;
        onChange?.([...displayAttachments, newAttachment]);
        onAttachmentAdded?.(newAttachment);
        message.success('Adjunto guardado correctamente');
      } else {
        await fileStorageService.deleteFile(tokenData, {
          bucket: CYCLE_ATTACHMENTS_BUCKET,
          paths: [finalPath],
        }).catch(() => {});
        message.error(res?.message || 'Error al guardar el adjunto en la base de datos');
      }
    } catch (err: any) {
      message.error(err.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  }, [logId, logUid, cycleUid, logInputId, tokenData, company, displayAttachments, onChange, onAttachmentAdded, securePost]);

  const handleDelete = useCallback(async (attachment: AttachmentItem) => {
    setDeletingId(attachment.id);
    try {
      // Si es un archivo local diferido (aún no subido)
      if (attachment.rawFile) {
        if (attachment.file_url?.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.file_url);
        }
        onChange?.(displayAttachments.filter(a => a.id !== attachment.id));
        message.success('Adjunto removido');
        return;
      }

      // Si ya está en Supabase Storage
      if (attachment.storage_path) {
        await fileStorageService.deleteFile(tokenData, {
          bucket: CYCLE_ATTACHMENTS_BUCKET,
          paths: [attachment.storage_path],
        }).catch(() => {});
      }

      // Si ya está registrado en BD
      if (logId) {
        const res = await securePost('/manufacturing/delete-log-attachment', {
          company_uid: company.current?.uid,
          id: attachment.id,
          csrfToken: tokenData?.token?.uid,
        });

        if (res?.success || !res?.error) {
          onChange?.(displayAttachments.filter(a => a.id !== attachment.id));
          onAttachmentDeleted?.(attachment.id);
          message.success('Adjunto eliminado');
        } else {
          message.error(res?.message || 'Error al eliminar el adjunto de la BD');
        }
      } else {
        onChange?.(displayAttachments.filter(a => a.id !== attachment.id));
        message.success('Adjunto eliminado');
      }
    } catch (err: any) {
      message.error(err.message || 'Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  }, [tokenData, displayAttachments, onChange, onAttachmentDeleted, logId]);

  return (
    <div>
      {label && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          <PaperClipOutlined style={{ marginRight: 4 }} />
          {label}
        </Text>
      )}

      {/* Lista de adjuntos existentes */}
      {displayAttachments.length > 0 && (
        <List
          size="small"
          dataSource={displayAttachments}
          style={{ marginBottom: 8 }}
          renderItem={(item) => (
            <List.Item
              style={{ padding: '4px 0', borderBottom: 'none' }}
              actions={[
                <Tooltip title="Ver" key="view">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => window.open(item.file_url, '_blank')}
                    style={{ padding: '0 4px' }}
                  />
                </Tooltip>,
                canEdit && (
                  <Popconfirm
                    key="delete"
                    title="¿Eliminar este archivo?"
                    onConfirm={() => handleDelete(item)}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                    placement="left"
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={deletingId === item.id}
                      style={{ padding: '0 4px' }}
                    />
                  </Popconfirm>
                ),
              ].filter(Boolean)}
            >
              <Space size={4} align="center" style={{ overflow: 'hidden' }}>
                {item.file_type === 'pdf' ? (
                  <FilePdfOutlined style={{ color: '#f5222d', fontSize: 16 }} />
                ) : (
                  <Image
                    src={item.file_url}
                    width={24}
                    height={24}
                    style={{ objectFit: 'cover', borderRadius: 2 }}
                    preview={false}
                  />
                )}
                <Tag color={item.file_type === 'pdf' ? 'red' : 'blue'} style={{ margin: 0, fontSize: 11 }}>
                  {item.file_type === 'pdf' ? 'PDF' : 'IMG'}
                </Tag>
                <Text style={{ fontSize: 12, display: 'inline-block', maxWidth: 150 }} ellipsis={{ tooltip: item.file_name }}>
                  {item.file_name}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}

      {/* Botón de carga */}
      {canEdit && (
        <Upload
          accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={({ file }) => handleUpload(file as File)}
          disabled={uploading}
        >
          <Button
            size="small"
            type="dashed"
            icon={<UploadOutlined />}
            loading={uploading}
            style={{ fontSize: 11, width: '100%', height: 28 }}
          >
            {uploading ? 'Subiendo...' : 'Adjuntar'}
          </Button>
        </Upload>
      )}
    </div>
  );
};

export default CycleLogAttachments;
